import moment from 'moment-timezone';

export const Formats = {
  date: {
    mdy: 'M/D/YYYY',
    dmy: 'D/M/YYYY',
    ymd: 'YYYY/M/D',
    iso: 'YYYY-MM-DD',
    pretty: 'ddd, MMM D, YYYY',
    shortDay: 'D',
    shortDayName: 'ddd',
    shortMonth: 'MMM D',
  },
  time: {
    '12hr': 'h:mm',
    '24hr': 'HH:mm',
    '12hr_with_seconds': 'h:mm:ss',
    '24hr_with_seconds': 'HH:mm:ss',
    hr_meridiem: 'h A',
  },
  time_seconds: {
    '12hr': 'h:mm:ss',
    '24hr': 'HH:mm:ss',
    '12hr_with_seconds': 'h:mm:ss',
    '24hr_with_seconds': 'HH:mm:ss',
  },
  time_without_seconds: {
    '12hr': 'h:mm',
    '24hr': 'HH:mm',
    '12hr_with_seconds': 'h:mm',
    '24hr_with_seconds': 'HH:mm',
  },
  duration: {},
};
const InternalFormats = {
  datetime: 'YYYY-MM-DD HH:mm:ss',
  date: 'YYYY-MM-DD',
  time: 'HH:mm:ss',
};
const DefaultTimeFormats = {
  date: 'pretty',
  time: '12hr',
  duration: 'hms',
};

// we are going to *abuse* and re-use access to the global config from the assets/javascripts/time_utils.js
// so this is accessing window.TimeUtils.UserTimeFormats

/**
 * Formats a date or time according to the specified style and options.
 *
 * @param {string} style - The style to be applied for formatting. Must be one of the keys of the Formats object.
 * @param {Object} [options] - Optional parameters to customize the formatting.
 * @param {string} [options.format] - The specific format string to use. If not provided, defaults based on the style.
 * @param {boolean} [options.showTimezone] - Whether to show the timezone. Accepts 'name', 'offset', or false.
 * @param {boolean} [options.omitMeridiem] - If true, omits the AM/PM part from the 12-hour format.
 * @returns {string} - The formatted date or time string according to the specified style and options.
 *
 * @example
 * // Formats time in 12-hour format without timezone and meridiem
 * formatFor('time_seconds', { format: 'hh:mm:ss', omitMeridiem: true });
 * // -> 'hh:mm:ss'
 *
 * @example
 * // Formats time in 24-hour format with timezone name
 * formatFor('time_without_seconds', { showTimezone: 'name' });
 * // -> 'h:mm a z'
 */

export function formatFor(style, { format, showTimezone, omitMeridiem } = {}) {
  if (format === undefined) {
    let aType = style;

    if (style === 'time_seconds' || style === 'time_without_seconds') {
      aType = 'time';
    }

    format = TimeUtils.UserTimeFormats[aType] || DefaultTimeFormats[aType];
  }
  const meridiemFormat = /12hr/.test(format) && !omitMeridiem ? ' a' : '';
  let tzFormat;

  switch (showTimezone) {
    case 'name':
      tzFormat = ' z';
      break;
    case 'offset':
      tzFormat = ' Z';
      break;
    default:
      tzFormat = '';
  }
  const theFormat = Formats[style][format] || format;

  return `${theFormat}${meridiemFormat}${tzFormat}`;
}

// internal datetime parsing routines
/**
 * Parses a given input into a moment datetime object. The function can handle moment objects,
 * or strings, returning a moment object if the input is valid. If the input is
 * invalid or empty, the function returns undefined.
 *
 * @param {moment|string} input - The input to be parsed. Can be a moment object or a string representing a date and time.
 * @returns {moment|undefined} - A moment object if the input is valid, otherwise undefined.
 *
 * @example
 * // Parse a moment object and return a moment object
 * parseInternalDatetime(moment('2023-12-25'));
 *
 * @example
 * // Parse a valid datetime string and return a moment object
 * parseInternalDatetime('2023-12-25T15:30:00Z');
 *
 * @example
 * // Returns undefined for invalid input
 * parseInternalDatetime('not-a-date');
 */

export function parseInternalDatetime(input) {
  if (moment.isMoment(input)) {
    return moment(input);
  }
  if (!input || input === undefined) {
    return;
  }
  input = input.trim();

  const datetime = moment.parseZone(input);

  if (datetime.isValid()) {
    return datetime;
  }
}

/**
 * Parses a given input into a moment date object. The function can handle moment objects,
 * or strings, returning a moment object if the input is valid. If the input is
 * invalid or empty, the function returns undefined.
 *
 * @param {moment|string} input - The input to be parsed. Can be a moment object or a string representing a date.
 * @returns {moment|undefined} - A moment object if the input is valid, otherwise undefined.
 *
 * @example
 * // Parse a moment object and return a moment object
 * parseInternalDate(moment('2023-12-25'));
 *
 * @example
 * // Parse a valid date string and return a moment object
 * parseInternalDate('2023-12-25');
 *
 * @example
 * // Returns undefined for invalid input
 * parseInternalDate('not-a-date');
 */

export function parseInternalDate(input) {
  if (moment.isMoment(input)) {
    return input;
  }

  if (input) {
    input = input.trim();
  }

  const date = moment(input, InternalFormats.date);

  if (date.isValid()) {
    return date;
  }
}

/**
 * Parses a given input into a moment time object. The function can handle moment objects,
 * or strings, returning a moment object if the input is valid. If the input is
 * invalid or empty, the function returns undefined.
 *
 * @param {moment|string} input - The input to be parsed. Can be a moment object or a string representing a time.
 * @returns {moment|undefined} - A moment object if the input is valid, otherwise undefined.
 *
 * @example
 * // Parse a moment object representing time
 * parseInternalTime(moment('15:30:00', 'HH:mm:ss'));
 *
 * @example
 * // Parse a valid time string
 * parseInternalTime('15:30:00');
 *
 * @example
 * // Returns undefined for invalid input
 * parseInternalTime('not-a-time');
 */

export function parseInternalTime(input) {
  if (moment.isMoment(input)) {
    return input;
  }
  if (input) {
    input = input.trim();
  }

  const time = moment(input, InternalFormats.time);

  if (time.isValid()) {
    return time;
  }
}

/**
 * Retrieves the timezone offset for a given date. The offset can be formatted either as an abbreviation or as a full offset.
 * This function supports moment objects or date strings. The timezone and formatting style can be optionally specified.
 *
 * @param {moment|string} date - The date for which to get the offset. Can be a moment object or a string.
 * @param {Object} [options] - Optional parameters to customize the offset retrieval.
 * @param {string|null} [options.timezone] - The timezone to be considered for the date. If null, the local timezone is used.
 * @param {'abbr'|null} [options.style] - The format of the offset. 'abbr' for abbreviation, or null for full offset.
 * @returns {string} - The formatted timezone offset as a string.
 *
 * @example
 * // Get the full offset for a specific date and timezone.
 * getOffset('2023-12-25T15:30:00', { timezone: 'America/New_York' });
 * // -> '-05:00'
 *
 * @example
 * // Get the abbreviated offset name for a moment date object.
 * getOffset(moment.tz('2023-12-25T15:30:00', 'America/Los_Angeles'), { style: 'abbr' });
 * // -> 'PST'
 *
 * @example
 * // Get the full offset for the current date and local timezone
 * getOffset(moment());
 * // -> '-08:00'
 */

export function getOffset(date, { timezone = null, style = null } = {}) {
  const format = style === 'abbr' ? 'z' : 'Z';
  const datetime = moment.isMoment(date) ? date : moment.tz(date, timezone);

  return datetime.format(format);
}

/**
 * Creates a moment object with a specified timezone, combining separate date and time inputs.
 * If the date or time inputs are already moment objects, they are first formatted to strings.
 *
 * @param {moment|string} date - The date part of the datetime. Can be a moment object or a string.
 * @param {moment|string|null} time - The time part of the datetime. Can be a moment object, a string, or null.
 * @param {string} timezone - The timezone to be used for creating the datetime.
 * @returns {moment} - A moment datetime object representing the combined date and time in the specified timezone.
 *
 * @example
 * // Create a datetime object from date and time strings in a specific timezone
 * createWithZone('2023-12-25', '15:30:00', 'America/New_York');
 *
 * @example
 * // Create a datetime object from moment date and time objects in a specific timezone
 * const momentDate = moment('2023-12-25');
 * const momentTime = moment('15:30:00', 'HH:mm:ss');
 * createWithZone(momentDate, momentTime, 'Europe/Paris');
 *
 * @example
 * // Create a datetime object from a date string without a time component in a specific timezone
 * createWithZone('2023-12-25', null, 'Asia/Tokyo');
 */

export function createWithZone(date, time, timezone) {
  if (moment.isMoment(date)) {
    date = date.format(InternalFormats.date);
  }
  if (moment.isMoment(time)) {
    time = time.format(InternalFormats.time);
  }

  const datetimeString = time ? `${date} ${time}` : date;

  return moment.tz(datetimeString, timezone);
}

/**
 * Creates a moment object in a specified timezone, or in the local timezone if no timezone is specified.
 *
 * @param {moment|string} dt - The datetime to be converted. Can be a moment object or a string.
 * @param {string|null} [tz] - The timezone for the datetime. If null, the local timezone is used.
 * @returns {moment} - A moment object in the specified timezone.
 *
 * @example
 * // Convert a datetime string to a moment object in a specific timezone
 * datetimeWithZone('2023-12-25T15:30:00', 'America/New_York');
 *
 * @example
 * // Convert a datetime string to a moment object in the local timezone
 * datetimeWithZone('2023-12-25T15:30:00');
 */

export function datetimeWithZone(dt, tz) {
  return tz ? moment.tz(dt, tz) : moment(dt);
}

/**
 * Calculates the duration between two moment or Dayjs objects.
 *
 * @param {moment|Dayjs} start - The start datetime.
 * @param {moment|Dayjs} stop - The stop datetime.
 * @returns {moment.Duration} - The duration between the start and stop datetime as a Dayjs.Duration object.
 *
 * @example
 * // Calculate duration between two Dayjs datetime objects
 * const start = moment('2023-12-25T10:00:00');
 * const stop = moment('2023-12-25T15:30:00');
 * getDuration(start, stop);
 */

export function getDuration(start, stop) {
  return moment.duration(stop.diff(start));
}

// datetime formatting routines
/**
 * Formats a moment or Day.js date object according to the internal date format.
 * Returns null if the date is not provided or invalid.
 *
 * @param {moment|Dayjs|null} date - The Dayjs date object to be formatted.
 * @returns {string|null} - The formatted date string, or null if the input is null or invalid.
 *
 * @example
 * // Format a valid Dayjs date object
 * formatInternalDate(dayjs('Dec 25 2023'));
 * // -> '2023-12-25'
 *
 * @example
 * // Returns null for invalid or null input
 * formatInternalDate(null);
 * // -> null
 */

export function formatInternalDate(date) {
  if (!date) {
    return null;
  }

  return date.format(InternalFormats.date);
}

/**
 * Formats a moment time object according to the internal time format.
 * Returns null if the time is not provided or invalid.
 *
 * @param {moment|null} time - The moment time object to be formatted.
 * @returns {string|null} - The formatted time string, or null if the input is null or invalid.
 *
 * @example
 * // Format a valid moment time object
 * formatInternalTime(moment('1:30', 'HH:mm:ss'));
 * // -> '01:30:00'
 *
 * @example
 * // Returns null for invalid or null input
 * formatInternalTime(null);
 * // -> null
 */

export function formatInternalTime(time) {
  if (!time) {
    return null;
  }

  return time.format(InternalFormats.time);
}

/**
 * Formats a moment or Dayjs date object using a specified format. If the date is invalid or null, the function
 * returns null. The format is determined by the 'formatFor' function for 'date' style.
 *
 * @param {dayjs.Dayjs|moment|null} date - The Dayjs or moment date object to be formatted.
 * @returns {string|null} - The formatted date string, or null if the date is invalid or null.
 *
 * @example
 * // Format a valid Dayjs date object
 * formatDate(dayjs('2023-12-25'));
 * // -> 'Mon, Dec 25, 2023'
 *
 * @example
 * // Returns null for invalid or null input
 * formatDate(null);
 * // -> null
 */

export function formatDate(date) {
  if (!date) {
    return null;
  }
  const format = formatFor('date');
  const formattedDate = date.format(format);

  if (moment(formattedDate, format).inspect().indexOf('invalid') === -1) {
    return formattedDate;
  }

  return null;
}

/**
 * Formats a moment time object according to specified options. If the time is invalid or null, the function
 * returns null. The format is determined by the 'formatFor' function based on the style, which is decided by the
 * 'showSeconds' option. Timezone adjustments are made using the 'datetimeWithZone' function.
 *
 * @param {moment|null} time - The moment time object to be formatted.
 * @param {Object} [options] - Optional parameters to customize the formatting.
 * @param {'never'|'always'|'auto'|'user'} [options.showSeconds] - Controls how seconds are displayed.
 *  - 'never' - never show seconds: e.g. 2:30 PM
 *  - 'always' - always show seconds: e.g. 2:30:00 PM or 4:10:23 PM
 *  - 'auto' - if the time has non-zero seconds, then force showing seconds, otherwise force hiding seconds: e.g. 2:30 PM (with 0 seconds) or 4:10:23 PM
 *  - 'user' - the default, use whatever the user’s chosen time format states: e.g. 2:30 PM or 2:30:23 PM
 * @param {string|null} [options.timezone] - The timezone to be considered for formatting.
 * @param {boolean|null} [options.showTimezone] - Whether to show the timezone in the output.
 * @param {boolean|null} [options.omitMeridiem] - If true, omits the AM/PM part from the 12-hour format.
 * @returns {string|null} - The formatted time string, or null if the time is invalid or null.
 *
 * @example
 * // Format a time object to show seconds always, with timezone
 * formatTime(moment('15:30:45', 'HH:mm:ss'), { showSeconds: 'always', timezone: 'America/New_York', showTimezone: true });
 * // -> '6:30:45 pm'
 *
 * @example
 * // Format a time object without seconds and without timezone
 * formatTime(moment('15:30', 'HH:mm'), { showSeconds: 'never' });
 * // -> '3:30 pm'
 *
 * @example
 * // Returns null for invalid or null input
 * formatTime(null);
 * // -> null
 */

export function formatTime(time, { showSeconds = 'user', timezone, showTimezone, omitMeridiem } = {}) {
  if (!time) {
    return null;
  }

  let style = 'time';

  if (showSeconds === 'never') {
    style = 'time_without_seconds';
  } else if (showSeconds === 'always') {
    style = 'time_seconds';
  } else if (showSeconds === 'auto') {
    style = time.seconds() > 0 ? 'time_seconds' : 'time';
  } // withSeconds === undefined || withSeconds === 'user'

  const format = formatFor(style, { showTimezone, omitMeridiem });
  const formattedTime = datetimeWithZone(time, timezone).format(format);

  if (moment(formattedTime, format).inspect().indexOf('invalid') === -1) {
    return formattedTime;
  }

  return null;
}

/**
 * Formats a duration, which can be a moment duration object or a number of seconds, according to specified options.
 * If the duration is invalid, returns null or a specified blank value. Supports decimal and hh:mm[:ss] formats.
 *
 * @param {moment.duration|number} duration - The duration to be formatted, either as a moment.duration object or a number of seconds.
 * @param {Object} [options] - Optional parameters to customize the formatting.
 * @param {'never'|'always'|'auto'|'user'} [options.showSeconds] - Controls whether seconds are displayed.
 *  - 'never' - never show seconds: e.g. 2:30 PM
 *  - 'always' - always show seconds: e.g. 2:30:00 PM or 4:10:23 PM
 *  - 'auto' - if the time has non-zero seconds, then force showing seconds, otherwise force hiding seconds: e.g. 2:30 PM (with 0 seconds) or 4:10:23 PM
 *  - 'user' - the default, use whatever the user’s chosen time format states: e.g. 2:30 PM or 2:30:23 PM
 * @param {boolean} [options.alwaysShowSign] - If true, always shows a '+' sign for positive durations.
 * @param {string|null} [options.blank] - A specific string to return for zero or invalid durations.
 * @returns {string|null} - The formatted duration string, or the blank value if specified and applicable.
 *
 * @example
 * // Format a numeric duration (in seconds) to hh:mm format
 * formatDuration(3660, { showSeconds: 'never' });
 * // -> '1:01'
 *
 * @example
 * // Format a moment duration object to hh:mm:ss format, showing sign
 * formatDuration(moment.duration(4460, 'seconds'), { showSeconds: 'always', alwaysShowSign: true });
 * // -> '+1:14:20'
 *
 * @example
 * // Returns a blank value for invalid duration
 * formatDuration(null);
 * // -> null
 */

export function formatDuration(
  duration,
  { showSeconds = 'user', alwaysShowSign = false, blank, roundSeconds = false, durationDifference = false, style } = {},
) {
  if (typeof duration === 'number') {
    duration = moment.duration(duration, 'seconds');
  }

  if (!moment.isDuration(duration) || !duration.isValid()) {
    if (blank) {
      return blank;
    }

    return null;
  }

  if (duration.asSeconds() === 0 && blank) {
    return blank;
  }

  const format = style || formatFor('duration');

  if (format === 'decimal') {
    return duration.asHours().toFixed(2);
  }
  let sign = alwaysShowSign ? '+' : '';

  duration = duration.clone();
  const totalSeconds = duration.asSeconds();

  if (totalSeconds < 0) {
    sign = '-';
    duration = moment.duration(Math.abs(totalSeconds), 'seconds');
  }

  let hours = Math.floor(duration.asHours());

  duration.subtract({ hours });
  let minutes = Math.floor(duration.asMinutes());

  duration.subtract({ minutes });
  const seconds = Math.floor(duration.asSeconds());
  let withSeconds = false;

  if (showSeconds === 'never') {
    withSeconds = false;
  } else if (showSeconds === 'always' || format === 'hms') {
    withSeconds = true;
  } else if (showSeconds === 'auto') {
    withSeconds = seconds > 0;
  } // showSeconds == undefined || showSeconds == 'user'

  // Needed when we calculate time difference
  // and have 10:00:00 - 1:30:30 = 8:29:30
  // so after rounding its 1:31 and 8:29 instead of 8:30
  const durationThresholdMet = seconds > (durationDifference ? 30 : 29);

  // Perform rounding logic for seconds
  if (!withSeconds && roundSeconds && durationThresholdMet) {
    if (minutes === 59) {
      // If minutes roll over an hour
      minutes = 0;
      hours += 1;
    } else {
      minutes += 1; // Increment minutes
    }
  }

  const hoursPart = hours.toString();
  const minutesPart = (minutes < 10 ? '0' : '') + minutes.toString();
  const secondsPart = (seconds < 10 ? '0' : '') + seconds.toString();

  if (format === 'units') {
    return `${sign}${hoursPart}h ${minutesPart}m${withSeconds ? ' ' + secondsPart + 's' : ''}`;
  }

  return `${sign}${hoursPart}:${minutesPart}${withSeconds ? ':' + secondsPart : ''}`;
}

/**
 * Formats a moment datetime object according to specified style and options.
 * If the datetime object is not provided, returns a specified blank value.
 * The function can format the object as a date, time, or a combination of both, with options for timezone and seconds display.
 *
 * @param {moment|null} dateOrTime - The moment datetime object to be formatted.
 * @param {Object} [options] - Optional parameters to customize the formatting.
 * @param {'date'|'time'|null} [options.style] - The formatting style: 'date', 'time', or null for both date and time.
 * @param {string|null} [options.timezone] - The timezone to be used for formatting.
 * @param {boolean|null} [options.showTimezone] - Whether to show the timezone in the output.
 * @param {'never'|'always'|'auto'|'user'} [options.showSeconds] - Controls whether seconds are displayed in the time.
 *  - 'never' - never show seconds: e.g. 2:30 PM
 *  - 'always' - always show seconds: e.g. 2:30:00 PM or 4:10:23 PM
 *  - 'auto' - if the time has non-zero seconds, then force showing seconds, otherwise force hiding seconds: e.g. 2:30 PM (with 0 seconds) or 4:10:23 PM
 *  - 'user' - the default, use whatever the user’s chosen time format states: e.g. 2:30 PM or 2:30:23 PM
 * @param {string} [options.blank] - A specific string to return if the input is null or invalid. Default is 'N/A'.
 * @returns {string} - The formatted datetime string, or the blank value if the input is null or invalid.
 *
 * @example
 * // Format a datetime object as both date and time, in a specific timezone
 * const datetime = moment('2023-12-25T15:30:00');
 * formatDatetime(datetime, { timezone: 'America/New_York' });
 * // -> 'Mon, Dec 25, 2023 6:30 pm'
 *
 * @example
 * // Format only the time part of a datetime object, showing seconds
 * formatDatetime(datetime, { style: 'time', showSeconds: 'always' });
 * // -> ''3:30:00 pm''
 *
 * @example
 * // Returns the blank value for null input
 * formatDatetime(null, { blank: 'Unavailable' });
 * // -> 'Unavailable'
 */

export function formatDatetime(
  dateOrTime,
  { style, timezone, showTimezone, showSeconds = 'user', blank = 'N/A' } = {},
) {
  // default datetime
  if (!dateOrTime) {
    return blank;
  }
  if (timezone) {
    dateOrTime = dateOrTime.clone().tz(timezone);
  }
  const formattedDate = formatDate(dateOrTime);
  const formattedTime = formatTime(dateOrTime, { showSeconds, timezone, showTimezone });
  let formattedDatetime;

  switch (style) {
    case 'date':
      formattedDatetime = formattedDate;
      break;
    case 'time':
      formattedDatetime = formattedTime;
      break;
    default:
      formattedDatetime = `${formattedDate} ${formattedTime}`;
  }

  return formattedDatetime;
}

/**
 * Formats a range of datetimes, specified as start and stop dates. It supports a variety of formatting options,
 * including timezones, display of seconds, and deduplication of dates. If the start or stop date is not provided,
 * returns a specified blank value. The function can also format each datetime in an alternative timezone.
 *
 * @param {moment|null} startDate - The start datetime of the range.
 * @param {moment|null} stopDate - The stop datetime of the range.
 * @param {Object} [options] - Optional parameters to customize the formatting.
 * @param {'date'|'time'|null} [options.style] - The formatting style: 'date', 'time', or null for both date and time.
 * @param {string|null} [options.timezone] - The primary timezone for formatting.
 * @param {string|null} [options.altTimeZone] - An alternative timezone for additional formatting.
 * @param {boolean|null} [options.showTimezone] - Whether to show the timezone in the output.
 * @param {'never'|'always'|'auto'|'user'} [options.showSeconds] - Controls whether seconds are displayed in the time.
 *  - 'never' - never show seconds: e.g. 2:30 PM
 *  - 'always' - always show seconds: e.g. 2:30:00 PM or 4:10:23 PM
 *  - 'auto' - if the time has non-zero seconds, then force showing seconds, otherwise force hiding seconds: e.g. 2:30 PM (with 0 seconds) or 4:10:23 PM
 *  - 'user' - the default, use whatever the user’s chosen time format states: e.g. 2:30 PM or 2:30:23 PM
 * @param {string} [options.blank] - A specific string to return if either start or stop date is null. Default is 'N/A - N/A'.
 * @param {boolean} [options.dedupDate] - If true, avoids repeating the date part for same-day start and stop times.
 * @param {string} [options.separator] - The separator string between start and stop datetime formats. Default is '-'.
 * @returns {string} - The formatted datetime range string, or the blank value if either start or stop date is null.
 *
 * @example
 * // Format a datetime range with different start and stop dates
 * const startDate = moment('2023-12-25T10:00:00');
 * const stopDate = moment('2023-12-26T15:30:00');
 * formatDatetimeRange(startDate, stopDate, { timezone: 'America/New_York' });
 * // -> 'Mon, Dec 25, 2023 1:00 pm - Tue, Dec 26, 2023 6:30 pm'
 *
 * @example
 * // Format a same-day datetime range, deduplicating the date part
 * const sameDayStart = moment('2023-12-25T09:00:00');
 * const sameDayStop = moment('2023-12-25T17:00:00');
 * formatDatetimeRange(sameDayStart, sameDayStop, { dedupDate: true, timezone: 'Europe/Paris' });
 * // -> 'Mon, Dec 25, 2023 6:00 pm - Tue, Dec 26, 2023 2:00 am'
 *
 * @example
 * // Returns the blank value for invalid inputs
 * formatDatetimeRange(null, null, { blank: 'Unavailable' });
 * // -> 'Unavailable'
 */

export function formatDatetimeRange(
  startDate,
  stopDate,
  {
    style,
    timezone,
    altTimeZone,
    showTimezone,
    showSeconds = 'user',
    blank = 'N/A - N/A',
    dedupDate = false,
    separator = '-',
  } = {},
) {
  if (startDate && stopDate) {
    const formattedStartDate = formatDatetime(startDate, { style: 'date', timezone });
    const formattedStopDate = formatDatetime(stopDate, { style: 'date', timezone });
    const sameDay = formattedStartDate === formattedStopDate;

    if (dedupDate && style === 'date' && !timezone) {
      if (sameDay) {
        return formatDatetime(startDate, { style, timezone, showTimezone });
      }
    }
    let startStyle, stopStyle;
    const formattedStartDatetime = formatDatetime(startDate, { style, timezone, showTimezone, showSeconds });

    stopStyle = dedupDate && sameDay ? 'time' : style;
    const formattedStopDatetime = formatDatetime(stopDate, { style: stopStyle, timezone, showTimezone, showSeconds });

    if (altTimeZone) {
      const altTimeZoneStartDate = formatDatetime(startDate, { style: 'date', timezone: altTimeZone });
      const altTimeZoneStopDate = formatDatetime(startDate, { style: 'date', timezone: altTimeZone });

      if (dedupDate) {
        startStyle = formattedStartDate === altTimeZoneStartDate ? 'time' : style;
        stopStyle = formattedStopDate === altTimeZoneStopDate ? 'time' : style;
      }
      const altStart = formatDatetime(startDate, {
        style: startStyle,
        timezone: altTimeZone,
        showTimezone,
        showSeconds,
      });
      const altStop = formatDatetime(stopDate, { style: stopStyle, timezone: altTimeZone, showTimezone, showSeconds });

      return `${formattedStartDatetime} (${altStart}) ${separator} ${formattedStopDatetime} (${altStop})`;
    }

    return `${formattedStartDatetime} ${separator} ${formattedStopDatetime}`;
  }

  return blank;
}

/**
 * Formats a Day.js or moment date object for chart display, using 'MMM DD' format (e.g., 'Jan 01').
 * Returns null if the date is not provided or invalid.
 *
 * @param {Dayjs|moment|null} date - The Dayjs/moment date object to be formatted for chart display.
 * @returns {string|null} - The formatted date string in 'MMM DD' format, or null if the date is invalid or null.
 *
 * @example
 * // Format a valid Dayjs date object for chart display
 * formatChartDate(dayjs('2023-01-01'));
 * // -> 'Jan 01'
 *
 * @example
 * // Returns null for invalid or null input
 * formatChartDate(null);
 * // -> null
 */

export function formatChartDate(date) {
  if (!date) {
    return null;
  }

  return date.format('MMM DD');
}

/**
 * Formats a number of hours as a duration, with or without seconds.
 *
 * @param {number} hours - The number of hours to be formatted as a duration.
 * @param {Object} [config] - Optional configuration for formatting.
 * @param {'never'|'always'|'auto'|'user'} [config.showSeconds] - Controls whether seconds are displayed. Default is 'never'.
 *  - 'never' - never show seconds: e.g. 2:30
 *  - 'always' - always show seconds: e.g. 2:30:00 or 4:10:23
 *  - 'auto' - if the time has non-zero seconds, then force showing seconds, otherwise force hiding seconds: e.g. 2:30 (with 0 seconds) or 4:10:23
 *  - 'user' - the default, use whatever the user’s chosen time format states: e.g. 2:30 or 2:30:23
 * @returns {string} - The formatted duration string.
 *
 * @example
 * // Format a duration of 3 hours without seconds
 * formatHours(3);
 * // -> '3:00'
 *
 * @example
 * // Format a duration of 1.5 hours with seconds
 * formatHours(1.5, { showSeconds: 'always' });
 * // -> '1:30:00'
 */

export function formatHours(hours, config = { showSeconds: 'never' }) {
  return formatDuration(moment.duration(hours, 'hours'), config);
}

/**
 * Evaluates how many days are remaining until the end of the given period.
 *
 * @param {string} period The period to evaluate - 'month' or 'week'
 */

export function daysUntilEndOfPeriod(period) {
  return moment().endOf(period).diff(moment());
}

/**
 * Sets UTC as the timezone of a given moment instance` without
 * performing a conversion
 */

export function stripZone(time) {
  return moment(time).tz('UTC', true);
}

/**
 * Returns a list of all the weeks between 'from' and 'to' with their respective
 * start and end days (moment.js objects).
 *
 * Defaults the range to the current month if none is provided.
 *
 * E.g. [{ id: 1, start: <moment>, end: <moment> }]
 *
 * @param {string|moment} from The period to evaluate from
 * @param {string|moment} to The period to evaluate until
 * @param {boolean} includeFuture Whether to include the future or trim 'to' to today's date
 */

export function getWeeksInPeriod(from = null, to = null, includeFuture = true) {
  const start = from ? moment(from) : moment().startOf('month');
  let end;

  // Trim 'to' to today if we don't want weeks in the future
  if (!includeFuture && moment(start).diff(moment(), 'days') <= 0) {
    end = moment().add(1, 'day').endOf('day');
  } else {
    end = to ? moment(to) : moment().endOf('month');
  }

  const daysDiff = end.diff(start, 'days');
  const weeks = new Map();
  let i = 0;
  const current = start.clone();

  while (i < daysDiff) {
    if (!weeks.has(current.week())) {
      weeks.set(current.week(), {
        id: current.clone().format('x'),
        start: current.clone().startOf('week'),
        end: current.clone().endOf('week'),
      });
    }

    current.add(1, 'day');
    i++;
  }

  return [...weeks.values()];
}

/**
 * Converts a server-side weekday to an ISO weekday
 *
 * @param {number} weekday The server-side sent week day.
 */

export function toMomentWeekday(weekday) {
  if (!weekday || weekday > 6) {
    return 1;
  }

  return (weekday + 1) % 7;
}

/**
 * Converts seconds to "HH:mm" string 90000 => "25:00"
 *
 * @param {number} seconds Duration in seconds
 */

export function secondsToHours(seconds) {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  return [String(hours), String(minutes).padStart(2, '0')].join(':');
}

/**
 * Converts a time zone string (e.g., "Pacific/Kiritimati") into a tz_options object.
 *
 * @param {string} title - The description or title for the time zone.
 * @param {string} timeZoneFor - The time zone string (e.g., "Pacific/Kiritimati").
 * @returns {Object} Time zone object with `abbr`, `description`, `display_name`, `for`, `formatted_timezone`, and `offset`.
 */

// Port of tzinfo's Timezone#friendly_identifier, which TimeZoneSerializer exposes as `display_name`:
// "America/Argentina/Buenos_Aires" -> "America - Buenos Aires, Argentina".
export function friendlyTimeZoneName(identifier) {
  const [region, ...rest] = identifier.split('/');

  if (!rest.length) {return region;}

  const parts = rest.map((part) => {
    const spaced = part.replace(/_/g, ' ');

    if (!/[a-z]/.test(spaced)) {return spaced;}

    return spaced
      .replace(/([^M][a-z])([A-Z])/g, '$1 $2')
      .replace(/(M[a-bd-z])([A-Z])/g, '$1 $2')
      .replace(/([A-Z])([A-Z])/g, "$1'$2");
  });

  return `${region} - ${parts.reverse().join(', ')}`;
}

export function makeUserTimeZone(title, timeZoneFor, mode = 'user') {
  const timezone = moment.tz.zone(timeZoneFor);
  const offsetString = moment.tz(timeZoneFor).format('Z'); // e.g., "-04:00" or "+05:30"

  return {
    abbr: timezone.abbr(new Date().getTime()),
    description: title,
    mode,
    display_name: friendlyTimeZoneName(timezone.name),
    for: timeZoneFor,
    formatted_timezone: `(GMT${offsetString}) ${timeZoneFor}`,
    offset: offsetString,
  };
}
